import React, { useMemo } from "react";
import { useFormik } from "formik";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import { TextField } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { useFormikHelpers } from "hooks/useFormikHelpers";
import { formatCashFull } from "utils/formatCash";
import type { InvoiceDetail } from "utils/invoices/invoiceFormat";
import { invoiceDetailSchema } from "validations/sendInvoice";

export type LineItemFields = Pick<
  InvoiceDetail,
  "productOrService" | "description" | "quantity" | "amount"
>;

interface LineItemDialogProps {
  open: boolean;
  /** The line being edited, or null for a new one. */
  item: InvoiceDetail | null;
  currency: string;
  onSave: (fields: LineItemFields) => void;
  /** Absent for a new line, which has nothing to remove yet. */
  onRemove?: () => void;
  onClose: () => void;
}

const LINE_ITEM_SCHEMA = invoiceDetailSchema.pick([
  "productOrService",
  "description",
  "quantity",
  "amount",
]);

const BLANK_LINE_ITEM: LineItemFields = {
  productOrService: "",
  description: "",
  quantity: 1,
  amount: 0,
};

/** A price is typed, not stepped, so it hides the browser's arrows. */
const NO_SPIN_SX = {
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    { WebkitAppearance: "none", margin: 0 },
};

/**
 * One line item's fields, labelled and full width, for a card too narrow to
 * edit the line in place. The draft is its own form, so Cancel leaves the
 * invoice as it was.
 */
const LineItemDialog: React.FC<LineItemDialogProps> = ({
  open,
  item,
  currency,
  onSave,
  onRemove,
  onClose,
}) => {
  const { t } = useTranslation();

  const initialValues = useMemo<LineItemFields>(
    () =>
      item
        ? {
            productOrService: item.productOrService,
            description: item.description,
            quantity: item.quantity,
            amount: item.amount,
          }
        : BLANK_LINE_ITEM,
    [item],
  );

  const formik = useFormik<LineItemFields>({
    initialValues,
    enableReinitialize: true,
    validationSchema: LINE_ITEM_SCHEMA,
    onSubmit: onSave,
  });
  const { handleFormikHelperText } = useFormikHelpers(formik);

  const lineTotal =
    (Number(formik.values.quantity) || 0) * (Number(formik.values.amount) || 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="line-item-dialog-title"
      // Reset once the dialog has faded out, so reopening the same line after a
      // Cancel shows what the invoice holds, not the abandoned draft.
      slotProps={{ transition: { onExited: () => formik.resetForm() } }}
    >
      <DialogTitle id="line-item-dialog-title">
        {item ? t("Edit Line Item") : t("Add Line Item")}
      </DialogTitle>
      <DialogContent>
        <Stack gap="var(--space-5)">
          <TextField
            id="line-item-dialog-product"
            name="productOrService"
            label={t("Product or Service")}
            required
            fullWidth
            autoFocus
            value={formik.values.productOrService}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            errorText={handleFormikHelperText("productOrService") || undefined}
          />
          <TextField
            id="line-item-dialog-description"
            name="description"
            label={t("Description")}
            fullWidth
            multiline
            rows={3}
            value={formik.values.description}
            onChange={formik.handleChange}
          />
          <Inline gap="var(--space-4)" wrap={false} align="start">
            <TextField
              id="line-item-dialog-quantity"
              name="quantity"
              label={t("Quantity")}
              required
              type="number"
              fullWidth
              value={formik.values.quantity}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              errorText={handleFormikHelperText("quantity") || undefined}
              inputProps={{
                min: 0,
                onWheel: (event) => event.currentTarget.blur(),
              }}
            />
            <TextField
              id="line-item-dialog-amount"
              name="amount"
              label={t("Unit Price")}
              required
              type="number"
              fullWidth
              value={formik.values.amount}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              errorText={handleFormikHelperText("amount") || undefined}
              inputProps={{
                min: 0,
                onWheel: (event) => event.currentTarget.blur(),
              }}
              sx={NO_SPIN_SX}
            />
          </Inline>
          <Inline justify="between" gap="var(--space-3)">
            <Typography
              variant="body1"
              sx={{ color: "var(--color-text-secondary)" }}
            >
              {t("Total")}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{ fontVariantNumeric: "tabular-nums" }}
            >
              {formatCashFull(lineTotal, currency)}
            </Typography>
          </Inline>
        </Stack>
      </DialogContent>
      <DialogActions>
        {onRemove && (
          <Button
            variant="danger-outlined"
            onClick={onRemove}
            sx={{ marginInlineEnd: "auto" }}
          >
            {t("Remove")}
          </Button>
        )}
        <Button variant="default-outlined" onClick={onClose}>
          {t("Cancel")}
        </Button>
        <Button variant="primary-filled" onClick={() => formik.submitForm()}>
          {t("Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LineItemDialog;
